import { isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';

import { optionApi } from '@/api/option/index.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getPremiumFromQuoteLegs } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { fillReportReceived, getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import type { OptionExecutionData, OptionExecutionLegData } from '@/store/growl/state/option.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import { getQuote, selectOptionCurrency, selectOptionQuoteLegs } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { FxOption } from '@/store/option/state.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

import { monitorTimeout } from '../monitorTimeout';

interface ExecuteOptionBody {
  rfsId: string;
  quoteId: string;
  way: 'Ask' | 'Bid';
  email64NotificationList: string;
  userLocalDateTime: string;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
  executionClickByFrontTimestamp: number;
}

export interface ExecuteOptionParams {
  notificationEmails: string;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
  tileId: string;
  quoteId: string;
  rfsId: string;
  userLocalDateTime?: string;
  way: 'Ask' | 'Bid'; // Ask / Bid -> Way
  accountId: string;
}

export const executeOption = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<unknown, ExecuteOptionParams>({
    query: (body) => {
      const now = Date.now();
      return {
        url: '/fxOption/rfs/execute',
        method: 'POST',
        body: {
          lastQuoteFromBackTimestamp: body.lastQuoteFromBackTimestamp,
          lastQuoteReceivedByFrontTimestamp: body.lastQuoteReceivedByFrontTimestamp,
          executionClickByFrontTimestamp: now,
          email64NotificationList: toBase64(body.notificationEmails),
          quoteId: body.quoteId,
          rfsId: body.rfsId,
          userLocalDateTime: format(now, "yyyy-MM-dd'T'HH:mm:ss"),
          way: body.way,
        } satisfies ExecuteOptionBody,
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, rfsId } = request;
      const stopMonitor = monitorTimeout(rfsId, dispatch, {
        onExecFail: () => {
          dispatchOptionExecutionRejected(tileId, rfsId, dispatch);
        },
        onExecSuccess: () => {
          dispatchOptionExecutionSuccess(tileId, rfsId, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, rfsId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'optionDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.optionCurrency.currency1}/${execution.data.data.optionCurrency.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: rfsId, error }));
        dispatchOptionExecutionRejected(tileId, rfsId, dispatch);
      }
    },

    async onCacheEntryAdded(request, { cacheEntryRemoved, dispatch, getState }) {
      const state = getState() as AppState;

      const tileId = request.tileId;
      const option = optionSlice.selectors.getOptionTile(state, tileId);

      if (isNotDefined(option)) {
        return;
      }

      const data = createExecutionData(option, request);
      if (isNotDefined(data)) {
        return;
      }

      dispatch(
        GrowlSlice.actions.create({
          growlId: request.rfsId,
          growl: {
            type: 'optionDealExecution',
            data: {
              status: 'pending',
              tileId: tileId,
              productName: 'Vanilla',
              data: data,
            },
            createdAt: Date.now(),
            options: { closeable: false },
          },
        }),
      );
      dispatch(optionSlice.actions.executionPending({ tileId, executionId: request.rfsId }));

      const handleReceiveDealReply = buildHandleReceiveDealReply(request, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(request, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(request, dispatch);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);

      await cacheEntryRemoved;
      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
    },
  });
};

function createExecutionData(option: FxOption, request: ExecuteOptionParams): OptionExecutionData | undefined {
  const firstLeg = option.properties.legs[0];
  const tileId = option.properties.tileId;
  const quote = getQuote(option);
  const optionCurrency = selectOptionCurrency(option);

  if (isNotDefined(firstLeg)) {
    console.error('No leg');
    return undefined;
  }
  const type = firstLeg.type;
  if (isNotDefined(type)) {
    console.error('Missing type for tile', tileId);
    return;
  }
  if (isNotDefined(quote)) {
    console.error('Missing quote for execution', tileId);
    return;
  }

  const { currency1, currency2 } = optionCurrency;

  const premiumType = option.properties.premiumType;
  const currencyIndex = premiumType.premiumCurrency;
  const premiumCurrency = currencyIndex === 1 ? currency1 : currency2;

  const way = request.way;

  const legs = option.properties.legs.map((leg, legIndex): OptionExecutionLegData => {
    const quoteLegs = selectOptionQuoteLegs(option, leg.legId);
    const premium = getPremiumFromQuoteLegs(quoteLegs, premiumType);
    return {
      legIndex,
      side: leg.side,
      type: leg.type,
      strike: leg.strike,
      // TODO
      notional: leg.notional!,
      expiryDate: leg.expiryDate,
      expiryDateTenor: leg.expiryDateTenor,
      currency: leg.notionalCurrencyIndex === 1 ? currency1 : currency2,
      premium,
    };
  });

  const quoteCcy = currencyIndex === 1 ? quote.ccy1 : quote.ccy2;
  return {
    type,
    accountId: request.accountId,
    tradeDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    way,
    productType: option.type,
    legs,
    optionCurrency,
    premiumCurrency,
    premiumType,
    quoteCcy: quoteCcy,
  };
}

const buildHandleReceiveFillReport = (params: ExecuteOptionParams, dispatch: AppDispatch) => {
  return (fillReport: FillReport) => {
    if (fillReport.Execute.ExecutionId !== params.rfsId) {
      return;
    }
    dispatchFillReport(params.tileId, params.rfsId, fillReport.BackOfficeReply.TradeIds, dispatch);
  };
};

export function dispatchFillReport(tileId: string, rfsId: string, tradeIds: string[], dispatch: AppDispatch) {
  dispatch(fillReportReceived({ growlId: rfsId, tradeIds }));
  dispatch(optionApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(optionSlice.actions.executionSuccess({ tileId: tileId }));
}

const buildHandleReceiveDealReply = (params: ExecuteOptionParams, dispatch: AppDispatch) => {
  return (dealReply: DealReply) => {
    if (dealReply.Execute.ExecutionId !== params.rfsId) {
      return;
    }

    dispatchOptionExecutionSuccess(params.tileId, params.rfsId, dispatch);
  };
};

export function dispatchOptionExecutionSuccess(tileId: string, rfsId: string, dispatch: AppDispatch) {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );
  dispatch(optionSlice.actions.executionSuccess({ tileId }));

  setTimeout(() => {
    dispatch(optionSlice.actions.executionReset({ tileId }));
  }, 2 * SECOND_IN_MILLIS);
}

function buildHandleReceiveDealRejected(params: ExecuteOptionParams, dispatch: AppDispatch) {
  return (dealRejectedRfs: DealRejected) => {
    if (dealRejectedRfs.Execute.ExecutionId !== params.rfsId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: dealRejectedRfs.DealReject.ErrorCode,
      multipassErrorCode: dealRejectedRfs.DealReject.MultipassErrorCode,
      reason: dealRejectedRfs.DealReject.ErrorMessage,
      sessionId: dealRejectedRfs.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.rfsId, data, options: { closeable: true } }));
    dispatchOptionExecutionRejected(params.tileId, params.rfsId, dispatch);
  };
}

export function dispatchOptionExecutionRejected(tileId: string, rfsId: string, dispatch: AppDispatch) {
  dispatch(optionApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(optionSlice.actions.executionRejected({ tileId }));
  setTimeout(() => {
    dispatch(optionSlice.actions.executionReset({ tileId }));
  }, 2 * SECOND_IN_MILLIS);
}
